import {
  ChartFrequency,
  IComposableChartsWidget,
  IPostgreJobsAnalyticsPayload,
} from 'wisestep-api';

import { getCandidateJobStatus } from '../../../../analyticsPage/helper/postgreDrawerChartsList';
import {
  PotentialRevenueMetricChart,
  PotentialRevenueTable,
} from '../../../../composableAnalytics/BaseCharts/PotentialRevenue';
import { IBusinessData } from '../../../../crmSettings/customizeStageStatus/helper';
import { ChartScope } from '../../../types';
import { PotentialRevenueWidget, getChartFreqForTimePeriod } from '../../helper';
import { jobsTimePeriodList } from '../../Jobs/helper';

const getPotentialRevenueChartWidgetByTimeAndScope = (
  timePeriodId: string,
  scope: ChartScope,
  frequency: ChartFrequency = getChartFreqForTimePeriod[timePeriodId]
): Partial<Record<PotentialRevenueWidget, IComposableChartsWidget>> => {
  const defaultQuery: IPostgreJobsAnalyticsPayload = {
    category: 'revenue',
    filterDate: 'joiningDate',
    filters: [
      {
        filterName: 'STAGE_STATUS',
        values: [JSON.stringify({ stage: 'Joining', selectedStatuses: ['Joined'] })],
      },
    ],
    type: 'count-metric',
    range: timePeriodId,
    scope,
    frequency,
  };
  const defaultQueryPerm = {
    ...defaultQuery,
    filters: [...(defaultQuery.filters ?? []), { filterName: 'JOB_TYPE', values: ['Permanent'] }],
  };
  const defaultQueryCont = {
    ...defaultQuery,
    filters: [...(defaultQuery.filters ?? []), { filterName: 'JOB_TYPE', values: ['Contract'] }],
  };

  const widgetConfig = {
    [PotentialRevenueWidget.METRIC_NET_REVENUE]: {
      name: 'Net Revenue by Joining Date',
      filters: {},
      category: 'revenue',
      type: `metric_revenue_${timePeriodId}_${scope}`,
      component: (
        <PotentialRevenueMetricChart
          query={{ ...defaultQuery, type: 'total-metric-net-revenue' }}
          title="Net Revenue by Joining Date"
          widgetName={PotentialRevenueWidget.METRIC_NET_REVENUE}
        />
      ),
    },
    [PotentialRevenueWidget.METRIC_NET_REVENUE_PERM]: {
      name: 'Net Revenue by Joining Date - Permanent',
      filters: {},
      category: 'revenue',
      type: `metric_net_revenue_perm_${timePeriodId}_${scope}`,
      component: (
        <PotentialRevenueMetricChart
          query={{
            ...defaultQueryPerm,
            type: 'total-metric-net-revenue',
          }}
          title="Net Revenue by Joining Date - Permanent"
          widgetName={PotentialRevenueWidget.METRIC_NET_REVENUE_PERM}
        />
      ),
    },
    [PotentialRevenueWidget.METRIC_NET_REVENUE_CONT]: {
      name: 'Net Revenue by Joining Date - Contract',
      filters: {},
      category: 'revenue',
      type: `metric_net_revenue_cont_${timePeriodId}_${scope}`,
      component: (
        <PotentialRevenueMetricChart
          query={{
            ...defaultQueryCont,
            type: 'total-metric-net-revenue',
          }}
          title="Net Revenue by Joining Date - Contract"
          widgetName={PotentialRevenueWidget.METRIC_NET_REVENUE_CONT}
        />
      ),
    },
    [PotentialRevenueWidget.METRIC_GROSS_REVENUE]: {
      name: 'Gross Revenue by Joining Date',
      filters: {},
      category: 'revenue',
      type: `metric_gross_revenue_${timePeriodId}_${scope}`,
      component: (
        <PotentialRevenueMetricChart
          query={{ ...defaultQuery, type: 'total-metric-gross-revenue' }}
          title="Gross Revenue by Joining Date"
          widgetName={PotentialRevenueWidget.METRIC_GROSS_REVENUE}
        />
      ),
    },
    [PotentialRevenueWidget.METRIC_GROSS_REVENUE_PERM]: {
      name: 'Gross Revenue by Joining Date - Permanent',
      filters: {},
      category: 'revenue',
      type: `metric_gross_revenue_perm_${timePeriodId}_${scope}`,
      component: (
        <PotentialRevenueMetricChart
          query={{
            ...defaultQueryPerm,
            type: 'total-metric-gross-revenue',
          }}
          title="Gross Revenue by Joining Date - Permanent"
          widgetName={PotentialRevenueWidget.METRIC_GROSS_REVENUE_PERM}
        />
      ),
    },
    [PotentialRevenueWidget.METRIC_GROSS_REVENUE_CONT]: {
      name: 'Gross Revenue by Joining Date - Contract',
      filters: {},
      category: 'revenue',
      type: `metric_gross_revenue_cont_${timePeriodId}_${scope}`,
      component: (
        <PotentialRevenueMetricChart
          query={{
            ...defaultQueryCont,
            type: 'total-metric-gross-revenue',
          }}
          title="Gross Revenue by Joining Date - Contract"
          widgetName={PotentialRevenueWidget.METRIC_GROSS_REVENUE_CONT}
        />
      ),
    },
    [PotentialRevenueWidget.METRIC_NET_MARGIN_PERM]: {
      name: 'Net Margin by Joining Date - Permanent',
      filters: {},
      category: 'revenue',
      type: `metric_net_margin_perm_${timePeriodId}_${scope}`,
      component: (
        <PotentialRevenueMetricChart
          query={{
            ...defaultQueryPerm,
            type: 'total-metric-net-margin',
          }}
          title="Net Margin by Joining Date - Permanent"
          widgetName={PotentialRevenueWidget.METRIC_NET_MARGIN_PERM}
        />
      ),
    },
    [PotentialRevenueWidget.METRIC_NET_MARGIN_CONT]: {
      name: 'Net Margin by Joining Date - Contract',
      filters: {},
      category: 'revenue',
      type: `metric_net_margin_cont_${timePeriodId}_${scope}`,
      component: (
        <PotentialRevenueMetricChart
          query={{
            ...defaultQueryCont,
            type: 'total-metric-net-margin',
          }}
          title="Net Margin by Joining Date - Contract"
          widgetName={PotentialRevenueWidget.METRIC_NET_MARGIN_CONT}
        />
      ),
    },
    [PotentialRevenueWidget.TABLE_CANDIDATE]: {
      name: 'Potential Revenue for Joined stage Candidates - Candidate Wise',
      filters: {},
      category: 'revenue',
      type: `table_revenue_candidate_${timePeriodId}_${scope}`,
      component: (
        <PotentialRevenueTable
          query={{ ...defaultQuery, type: 'candidate-table' }}
          title="Potential Revenue for Joined stage Candidates - Candidate Wise"
          widgetName={PotentialRevenueWidget.TABLE_CANDIDATE}
          widgetType={`table_revenue_candidate_${timePeriodId}_${scope}`}
        />
      ),
    },
    [PotentialRevenueWidget.TABLE_AM_CONTRACT]: {
      name: 'Net Revenue for Joined stage Candidates - AM (Contract)',
      filters: {},
      category: 'revenue',
      type: `table_revenue_amc_${timePeriodId}_${scope}`,
      component: (
        <PotentialRevenueTable
          query={{
            ...defaultQueryCont,
            type: 'account-manager-table',
          }}
          title="Net Revenue for Joined stage Candidates - AM (Contract)"
          widgetName={PotentialRevenueWidget.TABLE_AM_CONTRACT}
          widgetType={`table_revenue_amc_${timePeriodId}_${scope}`}
        />
      ),
    },
    [PotentialRevenueWidget.TABLE_AM_PERMANENT]: {
      name: 'Net Revenue for Joined stage Candidates - AM (Permanent)',
      filters: {},
      category: 'revenue',
      type: `table_revenue_amp_${timePeriodId}_${scope}`,
      component: (
        <PotentialRevenueTable
          query={{
            ...defaultQueryPerm,
            type: 'account-manager-table',
          }}
          title="Net Revenue for Joined stage Candidates - AM (Permanent)"
          widgetName={PotentialRevenueWidget.TABLE_AM_PERMANENT}
          widgetType={`table_revenue_amp_${timePeriodId}_${scope}`}
        />
      ),
    },
    [PotentialRevenueWidget.TABLE_CLIENT_CONTRACT]: {
      name: 'Net Revenue for Joined stage Candidates - Client (Contract)',
      filters: {},
      category: 'revenue',
      type: `table_revenue_cc_${timePeriodId}_${scope}_postgre`,
      component: (
        <PotentialRevenueTable
          query={{
            ...defaultQueryCont,
            type: 'client-table',
          }}
          title="Potential Revenue for Joined stage Candidates - Client (Contract)"
          widgetName={PotentialRevenueWidget.TABLE_CLIENT_CONTRACT}
          widgetType={`table_revenue_cc_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [PotentialRevenueWidget.TABLE_CLIENT_PERMANENT]: {
      name: 'Net Revenue for Joined stage Candidates - Client (Permanent)',
      filters: {},
      category: 'revenue',
      type: `table_revenue_cp_${timePeriodId}_${scope}_postgre`,
      component: (
        <PotentialRevenueTable
          query={{
            ...defaultQueryPerm,
            type: 'client-table',
          }}
          title="Net Revenue for Joined stage Candidates - Client (Permanent)"
          widgetName={PotentialRevenueWidget.TABLE_CLIENT_PERMANENT}
          widgetType={`table_revenue_cp_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [PotentialRevenueWidget.TABLE_CLIENT_CONTACT_CONTRACT]: {
      name: 'Net Revenue for Joined stage Candidates - Client Contact (Contract)',
      filters: {},
      category: 'revenue',
      type: `table_revenue_spoc_cont_${timePeriodId}_${scope}_postgre`,
      component: (
        <PotentialRevenueTable
          query={{
            ...defaultQueryCont,
            type: 'client-contact-table',
          }}
          title="Net Revenue for Joined stage Candidates - Client Contact (Contract)"
          widgetName={PotentialRevenueWidget.TABLE_CLIENT_CONTACT_CONTRACT}
          widgetType={`table_revenue_spoc_cont_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [PotentialRevenueWidget.TABLE_CLIENT_CONTACT_PERMANENT]: {
      name: 'Net Revenue for Joined stage Candidates - Client Contact (Permanent)',
      filters: {},
      category: 'revenue',
      type: `table_revenue_spoc_perm_${timePeriodId}_${scope}_postgre`,
      component: (
        <PotentialRevenueTable
          query={{
            ...defaultQueryPerm,
            type: 'client-contact-table',
          }}
          title="Net Revenue for Joined stage Candidates - Client Contact (Permanent)"
          widgetName={PotentialRevenueWidget.TABLE_CLIENT_CONTACT_PERMANENT}
          widgetType={`table_revenue_spoc_perm_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
  };

  return widgetConfig;
};

const getPRWidgetsForBDefByTimeAndScope = (
  timePeriod: { id: string; label: string; filterDate?: string },
  scope: ChartScope,
  businessDefinition: IBusinessData,
  frequency: ChartFrequency = getChartFreqForTimePeriod[timePeriod.id]
): Partial<Record<PotentialRevenueWidget, IComposableChartsWidget>> => {
  const timePeriodId = timePeriod.id;
  const filterDate = 'modifiedOn';
  const businessDefinitionName = businessDefinition.businessDefinitionName;
  const filters = [
    {
      filterName: 'STAGE_STATUS',
      values: businessDefinition.stageStatusData.map((stageStatus) => JSON.stringify(stageStatus)),
    },
    {
      filterName: 'JOB_STATUS',
      values: [getCandidateJobStatus(businessDefinition).toUpperCase()],
    },
  ];
  const defaultQuery: IPostgreJobsAnalyticsPayload = {
    category: 'revenue',
    filters,
    type: 'count-metric',
    range: timePeriodId,
    filterDate,
    scope,
    frequency,
  };

  const widgetConfig = {
    [PotentialRevenueWidget.TABLE_CANDIDATE]: {
      name: `Potential Revenue Candidate Wise - ${businessDefinitionName}`,
      filters: {},
      category: 'revenue',
      type: `table_revenue_candidate_${timePeriodId}_${scope}_${businessDefinition.id}_${filterDate}`,
      component: (
        <PotentialRevenueTable
          query={{ ...defaultQuery, type: 'candidate-table' }}
          title={`Potential Revenue Candidate Wise - ${businessDefinitionName}`}
          widgetName={PotentialRevenueWidget.TABLE_CANDIDATE_BDEF}
          widgetType={`table_revenue_candidate_${timePeriodId}_${scope}_${businessDefinition.id}_${filterDate}`}
        />
      ),
    },
  };

  return widgetConfig;
};

export const potentialRevenueStaticChartsKey = (
  timePeriodId: string,
  scope: ChartScope
): IComposableChartsWidget[] => {
  const defaultWidgetList = Object.values(
    getPotentialRevenueChartWidgetByTimeAndScope(timePeriodId, scope)
  );
  const extraWidgetList = {};

  return [...defaultWidgetList, ...(extraWidgetList[timePeriodId] ?? [])];
};

export const potentialRevenueForBDefKeys = (
  timePeriod: { id: string; label: string; filterDate?: string },
  scope: ChartScope,
  allBusinessDefinition: IBusinessData[] = []
): IComposableChartsWidget[] => {
  return allBusinessDefinition.flatMap((businessDefinition) =>
    Object.values(getPRWidgetsForBDefByTimeAndScope(timePeriod, scope, businessDefinition))
  );
};

export const potentialRevenueDashboardKeysObjectList = jobsTimePeriodList.flatMap((timePeriod) => [
  ...potentialRevenueStaticChartsKey(timePeriod.id, ChartScope.WORKSPACE),
  ...potentialRevenueStaticChartsKey(timePeriod.id, ChartScope.LOGGED_IN_USER),
  ...potentialRevenueStaticChartsKey(timePeriod.id, ChartScope.TEAMS),
]);
